import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  ArrowLeft, Building2, User, Clock, Send, Loader2,
  AlertTriangle, MessageSquare, Lock, Crown, Star, Paperclip, X, FileText, Image, File, Clipboard, Users
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const TicketDetail = () => {
  const { isDark = true } = useOutletContext() || {};
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { api, admin } = useSuperAdmin();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [adminsList, setAdminsList] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
        setTicket(res.data);
      } catch (err) {
        console.error('Failed to fetch ticket:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [api, ticketId]);

  const updateTicket = async (field, value) => {
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}`, { [field]: value });
      setTicket(prev => ({ ...prev, [field]: value }));
    } catch (err) {
      console.error('Failed to update ticket:', err);
    } finally {
      setSaving(false);
    }
  };

  // Fetch admins for assignment modal
  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const res = await api.get('/api/super-admin/admins');
      setAdminsList(res.data);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  // Open assignment modal
  const openAssignModal = () => {
    fetchAdmins();
    setSelectedAdminId(ticket?.assigned_to_id || '');
    setShowAssignModal(true);
  };

  // Assign ticket to selected admin
  const handleAssignTicket = async () => {
    if (!selectedAdminId) return;
    
    const selectedAdmin = adminsList.find(a => a.id === selectedAdminId);
    if (!selectedAdmin) return;
    
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}`, { 
        assigned_to_id: selectedAdmin.id,
        assigned_to_name: selectedAdmin.full_name
      });
      setTicket(prev => ({ 
        ...prev, 
        assigned_to_id: selectedAdmin.id,
        assigned_to_name: selectedAdmin.full_name
      }));
      setShowAssignModal(false);
    } catch (err) {
      console.error('Failed to assign ticket:', err);
    } finally {
      setSaving(false);
    }
  };

  // Unassign ticket
  const handleUnassignTicket = async () => {
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}`, { 
        assigned_to_id: null,
        assigned_to_name: null
      });
      setTicket(prev => ({ 
        ...prev, 
        assigned_to_id: null,
        assigned_to_name: null
      }));
      setShowAssignModal(false);
    } catch (err) {
      console.error('Failed to unassign ticket:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle paste event for images
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        if (file.size > 5 * 1024 * 1024) {
          alert('Pasted image is too large. Maximum size is 5MB.');
          continue;
        }

        setUploading(true);
        try {
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
          setAttachments(prev => [...prev, {
            filename: `pasted-screenshot-${timestamp}.png`,
            url: dataUrl,
            content_type: file.type,
            size: file.size,
            isPasted: true
          }]);
        } catch (err) {
          console.error('Failed to read pasted image:', err);
        } finally {
          setUploading(false);
        }
      }
    }
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newAttachments = [];
    
    for (const file of files) {
      // For now, we'll convert to base64 data URL (for small files)
      // In production, you'd upload to S3/cloud storage
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }
      
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newAttachments.push({
        filename: file.name,
        url: dataUrl,
        content_type: file.type,
        size: file.size
      });
    }
    
    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const addComment = async () => {
    if (!newComment.trim() && attachments.length === 0) return;
    setSendingComment(true);
    try {
      await api.post(`/api/super-admin/tickets/${ticketId}/comments`, {
        content: newComment,
        is_internal: isInternal,
        attachments: attachments.length > 0 ? attachments : null
      });
      // Refresh ticket
      const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
      setTicket(res.data);
      setNewComment('');
      setIsInternal(false);
      setAttachments([]);
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-500/20 text-purple-400 border-purple-500';
      case 'open': return 'bg-blue-500/20 text-blue-400 border-blue-500';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      case 'waiting_on_customer': return 'bg-orange-500/20 text-orange-400 border-orange-500';
      case 'waiting_on_support': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500';
      case 'resolved': return 'bg-green-500/20 text-green-400 border-green-500';
      case 'closed': return 'bg-slate-500/20 text-slate-400 border-slate-500';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      default: return 'text-slate-400';
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'professional':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            <Crown className="h-3 w-3 mr-1" />
            Professional
          </Badge>
        );
      case 'advanced':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Star className="h-3 w-3 mr-1" />
            Advanced
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
            Basic
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Ticket not found</p>
        <Button variant="ghost" onClick={() => navigate('/super-admin/tickets')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tickets
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/super-admin/tickets')}>
          <ArrowLeft className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-[#ffd108] font-mono">{ticket.ticket_number}</span>
            <Badge className={getStatusColor(ticket.status)}>
              {ticket.status?.replace(/_/g, ' ')}
            </Badge>
            {getTierBadge(ticket.tenant_tier)}
          </div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</h1>
          <div className={`flex items-center gap-4 mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            <span className="flex items-center gap-1">
              <Building2 className="h-4 w-4" /> {ticket.company_name}
            </span>
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" /> {ticket.submitted_by_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" /> {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{ticket.description}</p>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <MessageSquare className="h-5 w-5" /> Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.comments?.length === 0 ? (
                <p className={`text-center py-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>No comments yet</p>
              ) : (
                <div className="space-y-4">
                  {ticket.comments?.map((comment) => (
                    <div
                      key={comment.id}
                      className={`p-4 rounded-lg ${
                        comment.is_internal
                          ? 'bg-yellow-500/10 border border-yellow-500/30'
                          : comment.author_type === 'support'
                          ? 'bg-[#ffd108]/10 border border-[#ffd108]/30'
                          : isDark ? 'bg-slate-700/50' : 'bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{comment.author_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {comment.author_type === 'support' ? 'Support' : 'Customer'}
                          </Badge>
                          {comment.is_internal && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                              <Lock className="h-3 w-3 mr-1" /> Internal
                            </Badge>
                          )}
                        </div>
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {format(new Date(comment.created_at), 'PPp')}
                        </span>
                      </div>
                      <p className={`whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{comment.content}</p>
                      
                      {/* Comment Attachments */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {comment.attachments.map((file, idx) => {
                            const FileIcon = getFileIcon(file.content_type);
                            const isImage = file.content_type?.startsWith('image/');
                            return (
                              <div key={idx}>
                                {isImage ? (
                                  <a 
                                    href={file.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img 
                                      src={file.url} 
                                      alt={file.filename}
                                      className="max-w-sm rounded-lg border border-slate-600 hover:opacity-90 transition-opacity"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={file.url}
                                    download={file.filename}
                                    className={`inline-flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                                  >
                                    <FileIcon className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                                    <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{file.filename}</span>
                                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>({formatFileSize(file.size)})</span>
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment */}
              <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="relative">
                  <Textarea
                    ref={textareaRef}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onPaste={handlePaste}
                    placeholder="Add a comment... (Tip: You can paste screenshots directly!)"
                    className={`mb-3 pr-10 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    rows={3}
                  />
                  <div className="absolute right-3 top-3">
                    <Clipboard className={`h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} title="Paste images with Ctrl+V" />
                  </div>
                </div>
                
                {/* Attachments Preview - show pasted images inline */}
                {attachments.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {attachments.map((file, index) => {
                      const FileIcon = getFileIcon(file.content_type);
                      const isImage = file.content_type?.startsWith('image/');
                      
                      // Show pasted images inline
                      if (isImage && file.isPasted) {
                        return (
                          <div key={index} className="relative inline-block">
                            <img 
                              src={file.url} 
                              alt={file.filename}
                              className={`max-w-xs rounded-lg border ${isDark ? 'border-slate-600' : 'border-gray-300'}`}
                            />
                            <button
                              onClick={() => removeAttachment(index)}
                              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <span className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-2 py-1 rounded">
                              Pasted screenshot
                            </span>
                          </div>
                        );
                      }
                      
                      return (
                        <div 
                          key={index}
                          className={`flex items-center gap-3 p-2 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}
                        >
                          <FileIcon className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{file.filename}</p>
                            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            onClick={() => removeAttachment(index)}
                            className={`p-1 rounded hover:bg-red-500/20 ${isDark ? 'text-slate-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="internal"
                        checked={isInternal}
                        onCheckedChange={setIsInternal}
                      />
                      <Label htmlFor="internal" className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        Internal note
                      </Label>
                    </div>
                    
                    {/* File Attachment Button */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className={isDark ? 'border-slate-600 text-slate-400 hover:text-white' : 'border-gray-200 text-gray-500 hover:text-gray-900'}
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><Paperclip className="h-4 w-4 mr-1" /> Attach</>
                      )}
                    </Button>
                  </div>
                  
                  <Button
                    onClick={addComment}
                    disabled={(!newComment.trim() && attachments.length === 0) || sendingComment}
                    className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                  >
                    {sendingComment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Send</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</Label>
                <Select
                  value={ticket.status}
                  onValueChange={(v) => updateTicket('status', v)}
                  disabled={saving}
                >
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                    <SelectItem value="waiting_on_support">Waiting on Support</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Priority</Label>
                <Select
                  value={ticket.priority}
                  onValueChange={(v) => updateTicket('priority', v)}
                  disabled={saving}
                >
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">
                      <span className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" /> Urgent
                      </span>
                    </SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Category</Label>
                <p className={`capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.category?.replace(/_/g, ' ')}</p>
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Assigned To</Label>
                <div className="flex items-center gap-2">
                  <User className={`h-4 w-4 ${ticket.assigned_to_id === admin?.id ? 'text-[#ffd108]' : isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  <span className={`${ticket.assigned_to_id === admin?.id ? 'text-[#ffd108] font-medium' : isDark ? 'text-white' : 'text-gray-900'}`}>
                    {ticket.assigned_to_name 
                      ? (ticket.assigned_to_id === admin?.id ? 'Me' : ticket.assigned_to_name)
                      : 'Unassigned'
                    }
                  </span>
                </div>
              </div>

              <div className={`pt-4 border-t space-y-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{format(new Date(ticket.created_at), 'PP')}</span>
                </div>
                {ticket.resolved_at && (
                  <div className="flex justify-between text-sm">
                    <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Resolved</span>
                    <span className="text-green-400">{format(new Date(ticket.resolved_at), 'PP')}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/super-admin/tenants/${ticket.client_id}`)}
              >
                <Building2 className="mr-2 h-4 w-4" /> View Tenant
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={async () => {
                  setSaving(true);
                  try {
                    await api.put(`/api/super-admin/tickets/${ticketId}`, { 
                      assigned_to_id: admin?.id,
                      assigned_to_name: admin?.full_name
                    });
                    setTicket(prev => ({ 
                      ...prev, 
                      assigned_to_id: admin?.id,
                      assigned_to_name: admin?.full_name
                    }));
                  } catch (err) {
                    console.error('Failed to assign ticket:', err);
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving || ticket.assigned_to_id === admin?.id}
              >
                <User className="mr-2 h-4 w-4" /> 
                {ticket.assigned_to_id === admin?.id ? 'Assigned to Me' : 'Assign to Me'}
              </Button>
              {/* Assign to button - only for managers and admins */}
              {(admin?.role === 'manager' || admin?.role === 'admin') && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={openAssignModal}
                  disabled={saving}
                >
                  <Users className="mr-2 h-4 w-4" /> Assign to...
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assignment Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Assign Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              Select a team member to assign this ticket to:
            </p>
            {loadingAdmins ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
              </div>
            ) : (
              <Select value={selectedAdminId} onValueChange={setSelectedAdminId}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}>
                  <SelectValue placeholder="Select a team member" />
                </SelectTrigger>
                <SelectContent>
                  {adminsList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{a.full_name}</span>
                        <Badge variant="outline" className="text-xs ml-2 capitalize">{a.role}</Badge>
                        {a.id === admin?.id && <span className="text-xs text-[#ffd108]">(Me)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            {ticket?.assigned_to_id && (
              <Button
                variant="outline"
                onClick={handleUnassignTicket}
                disabled={saving}
                className="mr-auto"
              >
                Unassign
              </Button>
            )}
            <Button variant="ghost" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignTicket} 
              disabled={!selectedAdminId || saving}
              className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetail;
